/**
 * D89 / Piece B — Post Composer (Facebook + Instagram).
 *
 * Flow, top to bottom:
 *   1. Pick a Client (Client Group) — drives the account list.
 *   2. Pick which connected accounts to post to (Facebook Pages + their
 *      linked IG business accounts), with Select-all, search, and a
 *      per-account FB/IG/both toggle.
 *   3. Compose: caption (with IG 2,200-char warning) + media attach.
 *   4. Post type: Feed | Story | Reel.
 *   5. Save Draft (status=draft) or Continue (saves and hands off to
 *      Piece C — for now just a toast).
 *
 * The actual publishing pipeline lands in Piece C. This screen ends at
 * a saved `social_posts` row.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { socialAPI, clientGroupAPI, mediaAPI, toCurrentOrigin } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { StellarSelect } from '../components/StellarFormControls';
import {
  Image as ImageIcon, Film, Camera, Loader2, X, Upload, FileText, CheckCircle2,
  PenSquare, Plus, Search, Instagram, Facebook, AlertTriangle, Save, ArrowRight, Send, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { PublishResultsDialog } from '../components/PublishResultsDialog';
import { ImageCropDialog } from '../components/ImageCropDialog';

const NONE_SENTINEL = '__none__';
const IG_CAPTION_LIMIT = 2200;

const POST_TYPES = [
  { id: 'feed',  label: 'Feed Post', icon: PenSquare, desc: 'FB feed + IG feed' },
  { id: 'story', label: 'Story',     icon: Camera,    desc: '24-hour story' },
  { id: 'reel',  label: 'Reel',      icon: Film,      desc: 'IG reel — video only' },
];

// Map account.id -> {facebook: bool, instagram: bool} (the per-account
// platform choice). Stored at composer level so the user can mix:
// "post to all 40 FB pages but only 3 IGs", etc.
const defaultPlatformsFor = (acct, postType) => {
  // Reel only makes sense on IG (FB Reels via Graph isn't supported here yet).
  if (postType === 'reel') {
    return { facebook: false, instagram: !!acct?.ig_business_account_id };
  }
  return {
    facebook: true,
    instagram: !!acct?.ig_business_account_id,
  };
};

export default function PostComposerPage() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const fileInputRef = useRef(null);

  // ── Loading / data state ────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientsError, setClientsError] = useState(null);

  const [clientId, setClientId] = useState(NONE_SENTINEL);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState(null);

  // ── Composer state ─────────────────────────────────────────
  const [selected, setSelected] = useState({});       // {acct_id: true}
  const [platformMap, setPlatformMap] = useState({}); // {acct_id: {facebook, instagram}}
  const [acctFilter, setAcctFilter] = useState('');
  const [caption, setCaption] = useState('');
  const [postType, setPostType] = useState('feed');
  const [media, setMedia] = useState([]);            // [{id, type, url, filename, content_type}]
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  // D91 / Piece C — Publish flow
  const [publishingPostId, setPublishingPostId] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  // Scheduling (Piece D UI) — datetime-local string in the user's timezone,
  // converted to UTC ISO on save.
  const [scheduleAt, setScheduleAt] = useState('');
  // Image crop pipeline — queued files awaiting the crop dialog.
  const [cropFiles, setCropFiles] = useState([]);
  const [cropOpen, setCropOpen] = useState(false);

  // Deep link from the Content Calendar: /compose?client=<id>&date=YYYY-MM-DD
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const c = searchParams.get('client');
    const d = searchParams.get('date');
    if (c) setClientId(c);
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) setScheduleAt(`${d}T09:00`);
    // Media Library "Use in Composer" deep link
    const mUrl = searchParams.get('media_url');
    if (mUrl) {
      setMedia([{
        id: searchParams.get('media_id') || `lib-${Date.now()}`,
        upload_id: searchParams.get('media_id') || undefined,
        type: searchParams.get('media_type') === 'video' ? 'video' : 'image',
        url: mUrl,
        filename: searchParams.get('media_name') || 'library-media',
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load clients on workspace ready ─────────────────────────
  useEffect(() => {
    if (!wsId) return;
    setLoadingClients(true);
    setClientsError(null);
    clientGroupAPI.list(wsId)
      .then((r) => setClients(sortClientsAlpha(Array.isArray(r?.data) ? r.data : [])))
      .catch((e) => setClientsError(e?.response?.data?.detail || 'Failed to load clients'))
      .finally(() => setLoadingClients(false));
  }, [wsId]);

  // ── Load accounts when client selected ─────────────────────
  const loadAccounts = useCallback((cid) => {
    if (!wsId || !cid || cid === NONE_SENTINEL) {
      setAccounts([]);
      setSelected({});
      setPlatformMap({});
      return;
    }
    setLoadingAccounts(true);
    setAccountsError(null);
    const watchdog = setTimeout(() => {
      setAccountsError('Timed out loading accounts for this client.');
      setLoadingAccounts(false);
    }, 20_000);
    socialAPI.clientAccounts(wsId, cid)
      .then((r) => {
        clearTimeout(watchdog);
        const list = Array.isArray(r?.data?.accounts) ? r.data.accounts : [];
        setAccounts(list);
        // Pre-select all + default platforms (most common case is post-to-all).
        const sel = {};
        const pm = {};
        list.forEach((a) => {
          if (a?.id) {
            sel[a.id] = true;
            pm[a.id] = defaultPlatformsFor(a, postType);
          }
        });
        setSelected(sel);
        setPlatformMap(pm);
        setLoadingAccounts(false);
      })
      .catch((e) => {
        clearTimeout(watchdog);
        setAccountsError(e?.response?.data?.detail || 'Failed to load accounts');
        setLoadingAccounts(false);
      });
  }, [wsId, postType]);

  useEffect(() => { loadAccounts(clientId); }, [clientId, loadAccounts]);

  // When post type changes to reel, drop FB defaults on any unmodified accounts.
  useEffect(() => {
    setPlatformMap((prev) => {
      const next = { ...prev };
      accounts.forEach((a) => {
        if (!a?.id) return;
        if (postType === 'reel') {
          next[a.id] = { facebook: false, instagram: !!a.ig_business_account_id };
        }
      });
      return next;
    });
  }, [postType, accounts]);

  // ── Derived state ─────────────────────────────────────────
  const filteredAccounts = useMemo(() => {
    const f = acctFilter.trim().toLowerCase();
    if (!f) return accounts;
    return accounts.filter((a) =>
      ((a?.page_name) || '').toLowerCase().includes(f) ||
      ((a?.ig_username) || '').toLowerCase().includes(f) ||
      ((a?.category) || '').toLowerCase().includes(f),
    );
  }, [accounts, acctFilter]);

  const selectedAccounts = useMemo(
    () => accounts.filter((a) => a?.id && selected[a.id]),
    [accounts, selected],
  );

  const fbCount = useMemo(
    () => selectedAccounts.filter((a) => platformMap[a.id]?.facebook).length,
    [selectedAccounts, platformMap],
  );
  const igCount = useMemo(
    () => selectedAccounts.filter((a) => platformMap[a.id]?.instagram && a.ig_business_account_id).length,
    [selectedAccounts, platformMap],
  );

  const igSelected = igCount > 0;
  const captionOverIgLimit = igSelected && caption.length > IG_CAPTION_LIMIT;
  const allFilteredSelected = filteredAccounts.length > 0 && filteredAccounts.every((a) => selected[a.id]);

  const clientOptions = useMemo(
    () => [{ value: NONE_SENTINEL, label: 'Select a client…' }].concat(
      clients.map((c) => ({ value: c?.id || NONE_SENTINEL, label: c?.name || 'Unnamed' })),
    ),
    [clients],
  );

  // ── Mutations ─────────────────────────────────────────────
  const toggleAccount = (id, checked) => {
    setSelected((s) => ({ ...s, [id]: checked }));
    if (checked && !platformMap[id]) {
      const a = accounts.find((x) => x?.id === id);
      setPlatformMap((m) => ({ ...m, [id]: defaultPlatformsFor(a, postType) }));
    }
  };

  const toggleAllFiltered = () => {
    const next = { ...selected };
    const pm = { ...platformMap };
    if (allFilteredSelected) {
      filteredAccounts.forEach((a) => { delete next[a.id]; });
    } else {
      filteredAccounts.forEach((a) => {
        next[a.id] = true;
        if (!pm[a.id]) pm[a.id] = defaultPlatformsFor(a, postType);
      });
    }
    setSelected(next);
    setPlatformMap(pm);
  };

  const setPlatform = (acctId, key, value) => {
    setPlatformMap((m) => ({
      ...m,
      [acctId]: { ...(m[acctId] || { facebook: false, instagram: false }), [key]: value },
    }));
  };

  const uploadFileList = async (files) => {
    if (!wsId || !files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      try {
        const r = await mediaAPI.upload(wsId, f);
        const m = r?.data || {};
        if (!m.public_url) {
          toast.error(`Uploaded ${f.name} but it has no public URL`);
          continue;
        }
        uploaded.push({
          id: m.id,
          upload_id: m.id,
          type: (m.content_type || '').startsWith('video/') ? 'video' : 'image',
          url: toCurrentOrigin(m.public_url),
          filename: m.filename,
          content_type: m.content_type,
        });
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setMedia((arr) => [...arr, ...uploaded]);
    setUploading(false);
  };

  const handleFiles = (e) => {
    if (!wsId) return;
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const imgs = files.filter((f) => (f.type || '').startsWith('image/'));
    const vids = files.filter((f) => !(f.type || '').startsWith('image/'));
    // Videos skip the crop dialog — Meta handles aspect server-side.
    if (vids.length) uploadFileList(vids);
    if (imgs.length) { setCropFiles(imgs); setCropOpen(true); }
  };

  const removeMedia = (id) => setMedia((arr) => arr.filter((m) => m.id !== id));

  const buildPayload = (status) => {
    const targets = selectedAccounts.map((a) => {
      const plats = [];
      if (platformMap[a.id]?.facebook) plats.push('facebook');
      if (platformMap[a.id]?.instagram && a.ig_business_account_id) plats.push('instagram');
      return { social_account_id: a.id, platforms: plats };
    }).filter((t) => t.platforms.length > 0);
    return {
      workspace_id: wsId,
      client_group_id: clientId !== NONE_SENTINEL ? clientId : null,
      caption,
      media: media.map((m) => ({ type: m.type, url: m.url, upload_id: m.upload_id })),
      post_type: postType,
      targets,
      status,
    };
  };

  const validate = () => {
    if (clientId === NONE_SENTINEL) return 'Pick a client first';
    if (selectedAccounts.length === 0) return 'Pick at least one account';
    const targets = buildPayload('draft').targets;
    if (targets.length === 0) return 'Each selected account needs at least one platform (FB or IG)';
    if (postType === 'reel' && !media.some((m) => m.type === 'video')) return 'Reels require a video';
    if (postType === 'story' && media.length === 0) return 'Stories require media';
    if (captionOverIgLimit) return `IG caption limit is ${IG_CAPTION_LIMIT} characters`;
    return null;
  };

  const save = async (statusAfterSave) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (statusAfterSave === 'schedule') {
      if (!scheduleAt) { toast.error('Pick a schedule date & time first'); return; }
      const dt = new Date(scheduleAt);
      if (Number.isNaN(dt.getTime()) || dt.getTime() < Date.now() - 60000) {
        toast.error('Schedule time must be in the future');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = statusAfterSave === 'schedule'
        ? { ...buildPayload('scheduled'), status: 'scheduled', scheduled_at: new Date(scheduleAt).toISOString() }
        : buildPayload('draft');
      const r = await socialAPI.createPost(payload);
      const post = r?.data;
      if (statusAfterSave === 'publish') {
        // D91 / Piece C — Save then immediately publish.
        if (!post?.id) throw new Error('Saved post has no id');
        try {
          await socialAPI.publishPost(wsId, post.id);
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Publish kickoff failed');
          setSaving(false);
          return;
        }
        toast.success('Publishing started — watch progress in the dialog.');
        setPublishingPostId(post.id);
        setPublishOpen(true);
      } else if (statusAfterSave === 'schedule') {
        toast.success(`Scheduled for ${new Date(scheduleAt).toLocaleString()}`, {
          description: 'Track it in the Post Queue → Scheduled tab or the client Content Calendar.',
        });
        setScheduleAt('');
      } else if (statusAfterSave === 'continue') {
        toast.success('Draft saved — publish & schedule come next.', {
          description: post?.id ? `Post ID ${post.id.slice(0, 8)}…` : undefined,
        });
      } else {
        toast.success('Draft saved.', {
          description: post?.id ? `Post ID ${post.id.slice(0, 8)}…` : undefined,
        });
      }
      // Reset minimal state — keep client + accounts so the operator can
      // batch out another post for the same client.
      setCaption('');
      setMedia([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5 animate-fade-in-up" data-testid="post-composer-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Post Composer</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Compose Facebook + Instagram posts for a single client across all of their connected accounts.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ─── Main column: client + composer ─── */}
        <div className="lg:col-span-2 space-y-4">
          {/* 1) Client */}
          <Card data-testid="d89-client-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">1. Select a client</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {loadingClients ? (
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground" data-testid="d89-clients-loading">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading clients…
                </div>
              ) : clientsError ? (
                <div className="text-[12px] text-destructive" data-testid="d89-clients-error">{clientsError}</div>
              ) : clients.length === 0 ? (
                <div className="text-[12px] text-muted-foreground" data-testid="d89-clients-empty">
                  No clients yet. Create a client group first.
                </div>
              ) : (
                <StellarSelect
                  value={clientId}
                  onValueChange={setClientId}
                  options={clientOptions}
                  placeholder="Select a client…"
                  testId="d89-client-select"
                />
              )}
            </CardContent>
          </Card>

          {/* 2) Accounts */}
          {clientId !== NONE_SENTINEL && (
            <Card data-testid="d89-accounts-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">2. Accounts to post to</CardTitle>
                <span className="text-[11px] text-muted-foreground" data-testid="d89-account-counter">
                  Posting to <span className="text-foreground font-medium">{fbCount}</span> Facebook Page{fbCount === 1 ? '' : 's'} and{' '}
                  <span className="text-foreground font-medium">{igCount}</span> Instagram account{igCount === 1 ? '' : 's'}
                </span>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {loadingAccounts ? (
                  <div className="flex items-center justify-center py-6" data-testid="d89-accounts-loading">
                    <Loader2 className="h-5 w-5 animate-spin text-gold/50" />
                    <span className="ml-2 text-[12px] text-muted-foreground">Loading accounts…</span>
                  </div>
                ) : accountsError ? (
                  <div className="flex flex-col gap-2 items-center py-6" data-testid="d89-accounts-error">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <span className="text-[12px] text-foreground/80">{accountsError}</span>
                    <Button size="sm" onClick={() => loadAccounts(clientId)} data-testid="d89-accounts-retry">Try again</Button>
                  </div>
                ) : accounts.length === 0 ? (
                  <div className="text-[12px] text-muted-foreground py-6 text-center" data-testid="d89-accounts-empty">
                    This client has no connected Pages. Connect accounts on the{' '}
                    <a href="/integrations" className="text-gold underline">Integrations</a> page.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
                        <Input
                          value={acctFilter}
                          onChange={(e) => setAcctFilter(e.target.value)}
                          placeholder="Filter by page name, IG handle, or category…"
                          className="h-8 pl-7 text-[12px]"
                          data-testid="d89-account-filter"
                        />
                      </div>
                      <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={toggleAllFiltered}
                              data-testid="d89-select-all">
                        {allFilteredSelected ? 'Deselect all' : 'Select all'}
                        <span className="ml-1 text-muted-foreground">({filteredAccounts.length})</span>
                      </Button>
                    </div>
                    <div className="max-h-72 overflow-y-auto border border-border/30 rounded-md divide-y divide-border/20"
                         data-testid="d89-account-list">
                      {filteredAccounts.length === 0 ? (
                        <div className="p-4 text-[12px] text-muted-foreground text-center">No accounts match your filter.</div>
                      ) : filteredAccounts.map((a) => {
                        if (!a?.id) return null;
                        const sel = !!selected[a.id];
                        const pm = platformMap[a.id] || { facebook: false, instagram: false };
                        const hasIG = !!a.ig_business_account_id;
                        return (
                          <div key={a.id} className="flex items-center gap-2 p-2 hover:bg-foreground/[0.02]"
                               data-testid={`d89-account-row-${a.id}`}>
                            <input type="checkbox" checked={sel}
                                   onChange={(e) => toggleAccount(a.id, e.target.checked)}
                                   className="h-4 w-4 accent-gold"
                                   data-testid={`d89-account-check-${a.id}`} />
                            <div className="h-7 w-7 rounded-full bg-foreground/5 border border-border/40 overflow-hidden shrink-0 flex items-center justify-center">
                              {a.picture_url
                                ? <img src={a.picture_url} alt="" className="h-full w-full object-cover" />
                                : <ImageIcon className="h-3.5 w-3.5 text-muted-foreground/40" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[12px] font-medium truncate">{a.page_name || 'Untitled Page'}</div>
                              <div className="text-[10px] text-muted-foreground truncate">
                                {a.category || ''}{hasIG ? ` · @${a.ig_username || 'ig'}` : ''}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button type="button" disabled={!sel || postType === 'reel'}
                                      onClick={() => setPlatform(a.id, 'facebook', !pm.facebook)}
                                      title={postType === 'reel' ? 'Reels publish to IG only' : 'Facebook'}
                                      className={`h-7 px-2 rounded text-[10px] inline-flex items-center gap-1 border transition-colors ${
                                        sel && pm.facebook
                                          ? 'bg-[#1877F2]/15 border-[#1877F2]/60 text-[#4d94ff]'
                                          : 'border-border/30 text-muted-foreground/60 hover:border-border/60'
                                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                                      data-testid={`d89-toggle-fb-${a.id}`}>
                                <Facebook className="h-3 w-3" /> FB
                              </button>
                              <button type="button" disabled={!sel || !hasIG}
                                      onClick={() => setPlatform(a.id, 'instagram', !pm.instagram)}
                                      title={hasIG ? 'Instagram' : 'No IG linked to this Page'}
                                      className={`h-7 px-2 rounded text-[10px] inline-flex items-center gap-1 border transition-colors ${
                                        sel && pm.instagram && hasIG
                                          ? 'bg-[#E1306C]/15 border-[#E1306C]/60 text-pink-300'
                                          : 'border-border/30 text-muted-foreground/60 hover:border-border/60'
                                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                                      data-testid={`d89-toggle-ig-${a.id}`}>
                                <Instagram className="h-3 w-3" /> IG
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* 3) Post type */}
          {clientId !== NONE_SENTINEL && (
            <Card data-testid="d89-posttype-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">3. Post type</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 gap-2">
                  {POST_TYPES.map((pt) => {
                    const Icon = pt.icon;
                    const active = postType === pt.id;
                    return (
                      <button key={pt.id} type="button" onClick={() => setPostType(pt.id)}
                              className={`p-3 rounded-lg border text-center transition-all ${active
                                ? 'bg-gold/10 border-gold text-gold'
                                : 'bg-secondary/30 border-border hover:border-gold/30 text-muted-foreground'}`}
                              data-testid={`d89-posttype-${pt.id}`}>
                        <Icon className="h-5 w-5 mx-auto mb-1" />
                        <p className="text-xs font-medium">{pt.label}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{pt.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 4) Caption */}
          {clientId !== NONE_SENTINEL && (
            <Card data-testid="d89-caption-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">4. Caption</CardTitle>
                <span className={`text-[10px] ${captionOverIgLimit ? 'text-destructive' : 'text-muted-foreground'}`}
                      data-testid="d89-caption-counter">
                  {caption.length}{igSelected ? ` / ${IG_CAPTION_LIMIT}` : ''}
                </span>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <Textarea value={caption} onChange={(e) => setCaption(e.target.value)}
                          placeholder="Write your caption — hashtags, mentions, CTAs…"
                          className="min-h-[120px] resize-y" data-testid="d89-caption-input" />
                {captionOverIgLimit && (
                  <div className="flex items-center gap-1.5 text-[11px] text-destructive" data-testid="d89-caption-ig-warning">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Instagram caption limit is {IG_CAPTION_LIMIT} characters. Trim or untick Instagram.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* 5) Media */}
          {clientId !== NONE_SENTINEL && (
            <Card data-testid="d89-media-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-gold" /> 5. Media ({media.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*"
                       onChange={handleFiles} className="hidden" data-testid="d89-media-input" />
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
                  {media.map((m) => (
                    <div key={m.id} className="relative group aspect-square rounded-lg overflow-hidden bg-secondary border border-border"
                         data-testid={`d89-media-thumb-${m.id}`}>
                      {m.type === 'image'
                        ? <img src={m.url} alt={m.filename || ''} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex flex-col items-center justify-center bg-black/40">
                            <Film className="h-6 w-6 text-muted-foreground" />
                            <span className="text-[9px] text-muted-foreground mt-1 truncate max-w-full px-1">
                              {m.filename || 'Video'}
                            </span>
                          </div>}
                      <button type="button" onClick={() => removeMedia(m.id)}
                              className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`d89-media-remove-${m.id}`}>
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                          className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-gold/40 transition-colors flex flex-col items-center justify-center gap-1 disabled:opacity-50"
                          data-testid="d89-media-add">
                    {uploading ? <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" /> : <Plus className="h-5 w-5 text-muted-foreground" />}
                    <span className="text-[10px] text-muted-foreground">{uploading ? 'Uploading…' : 'Add Media'}</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ─── Sidebar: preview + actions ─── */}
        <div className="space-y-4">
          {/* Live preview */}
          <Card data-testid="d89-preview-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Preview</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="rounded-lg border border-border/30 bg-background/40 overflow-hidden">
                {media.length > 0 ? (
                  media[0].type === 'image'
                    ? <img src={media[0].url} alt="" className="w-full max-h-56 object-cover" />
                    : <div className="w-full h-40 flex items-center justify-center bg-black/40">
                        <Film className="h-8 w-8 text-muted-foreground" />
                      </div>
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-secondary/30 text-muted-foreground text-[11px]">
                    No media attached yet
                  </div>
                )}
                <div className="p-3 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{postType}</Badge>
                    {fbCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[#4d94ff]">
                        <Facebook className="h-3 w-3" />{fbCount}
                      </span>
                    )}
                    {igCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-pink-300">
                        <Instagram className="h-3 w-3" />{igCount}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] whitespace-pre-wrap break-words text-foreground/90">
                    {caption || <span className="text-muted-foreground italic">Caption preview…</span>}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card data-testid="d89-actions-card">
            <CardContent className="p-4 space-y-2">
              <Button className="w-full" variant="outline" onClick={() => save('draft')}
                      disabled={saving || uploading} data-testid="d89-save-draft">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save draft
              </Button>
              <Button className="w-full" onClick={() => save('publish')}
                      disabled={saving || uploading} data-testid="d91-publish-now">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Publish now
              </Button>
              {/* Piece D UI — Schedule for later */}
              <div className="pt-1 space-y-1.5 border-t border-border/30 mt-1" data-testid="d92-schedule-block">
                <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" /> Schedule for later (your local time)
                </Label>
                <Input type="datetime-local" value={scheduleAt}
                       onChange={(e) => setScheduleAt(e.target.value)}
                       min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                       className="h-8 text-[12px]" data-testid="d92-schedule-datetime" />
                <Button className="w-full" variant="secondary" onClick={() => save('schedule')}
                        disabled={saving || uploading || !scheduleAt} data-testid="d92-schedule-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
                  Schedule post
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center pt-1">
                <FileText className="h-3 w-3 inline mr-0.5" />
                Scheduled posts run automatically — manage them in the Post Queue or the client Content Calendar.
              </p>
            </CardContent>
          </Card>

          {/* Summary chip — useful at a glance with 40+ accounts. */}
          {selectedAccounts.length > 0 && (
            <Card data-testid="d89-summary-card">
              <CardContent className="p-3 space-y-1 text-[11px]">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  <span>{selectedAccounts.length} account{selectedAccounts.length === 1 ? '' : 's'} selected</span>
                </div>
                <div className="text-muted-foreground">{fbCount} FB · {igCount} IG · {media.length} media · {postType}</div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <PublishResultsDialog
        open={publishOpen}
        workspaceId={wsId}
        postId={publishingPostId}
        onClose={() => setPublishOpen(false)}
      />

      <ImageCropDialog
        open={cropOpen}
        files={cropFiles}
        lockRatio={cropFiles.length + media.filter((m) => m.type === 'image').length > 1}
        onDone={(outFiles) => { setCropOpen(false); setCropFiles([]); uploadFileList(outFiles); }}
        onCancel={() => { setCropOpen(false); setCropFiles([]); }}
      />
    </div>
  );
}
